import { uniqBy } from 'lodash/fp';
import { actionCreator, isType } from './utils/actions';

export const initialRendererState: RendererProviderStore = {
  selectedAnnotations: [],
  selectedCadItems: undefined,
  selectedWmsItem: undefined,
  selectedOrthophotoItem: undefined,
};

type SelectedAnnotation = { identifier: string; type: 'group' | 'annotation'; groupIdentifier?: string; instanceKey?: string };
type SelectedCadItem = { identifier: string; type: 'cadLayer' | 'cadObjectGroup' | 'cadObject' };
type SelectedWmsItem = { identifier: string };
type SelectedOrthophotoItem = { identifier: string };
/** A single selected skeleton sub-element (node or edge) in the layout editor. */
export type SelectedWallSketchElement = { annotationId: string; kind: 'node' | 'edge'; ids: string[] };
export interface RendererProviderStore {
  selectedAnnotations: SelectedAnnotation[];
  lastSelectedAnnotation?: SelectedAnnotation;
  selectedPointCloudName?: string;
  mapSelected?: boolean;
  editableNameAnnotation?: {
    id: string;
    name: string;
    type: 'annotation' | 'group' | 'pointcloud' | 'cadObject' | 'cadObjectGroup' | 'cadLayer' | 'wmsLayer' | 'orthophotoLayer';
    instanceKey?: string;
  };
  performanceWarning?: boolean;
  selectedCadItems?: SelectedCadItem[];
  selectedWmsItem?: SelectedWmsItem;
  selectedOrthophotoItem?: SelectedOrthophotoItem;
  /** Set while skeleton nodes/edges are selected in the layout editor; drives the repurposed right panel. */
  selectedWallSketchElement?: SelectedWallSketchElement;
}

export const toggleSelectedAnnotation = actionCreator<{
  identifier: string;
  type: 'group' | 'annotation';
  clearOtherSelections?: boolean;
  groupIdentifier?: string;
  instanceKey?: string;
}>('RENDERER_SELECTED_ANNOTATION_TOGGLE');
export const toggleSelectedCadItem = actionCreator<{
  identifier: string;
  type: 'cadLayer' | 'cadObjectGroup' | 'cadObject';
}>('RENDERER_SELECTED_CAD_ITEM');
export const toggleSelectedWmsItem = actionCreator<{
  identifier: string;
}>('RENDERER_SELECTED_WMS_ITEM');
export const toggleSelectedOrthophotoItem = actionCreator<{
  identifier: string;
}>('RENDERER_SELECTED_ORTHOPHOTO_ITEM');
export const mergeSelectedAnnotations = actionCreator<{
  annotations: SelectedAnnotation[];
}>('RENDERER_SELECTED_ANNOTATION_MERGE');
/** Replace the whole annotation selection (box select 'replace' mode). */
export const setSelectedAnnotations = actionCreator<{
  annotations: SelectedAnnotation[];
}>('RENDERER_SELECTED_ANNOTATION_SET');
/** Subtract annotations from the selection (box select 'remove' mode). */
export const removeSelectedAnnotations = actionCreator<{
  identifiers: string[];
}>('RENDERER_SELECTED_ANNOTATION_REMOVE');
export const selectPointCloud = actionCreator<{ name?: string }>('RENDERER_POINT_CLOUD_SELECT');
export const selectMap = actionCreator('RENDERER_MAP_SELECT');
export const unSelectAll = actionCreator('RENDERER_UN_SELECT_ALL');
export const selectEditableNameAnnotation = actionCreator<
  | {
      id: string;
      name: string;
      type: 'annotation' | 'group' | 'pointcloud' | 'cadObject' | 'cadObjectGroup' | 'cadLayer' | 'wmsLayer' | 'orthophotoLayer';
      instanceKey?: string;
    }
  | undefined
>('RENDERER_SELECT_EDITABLE_NAME_ANNOTATION');
export const updateEditableNameAnnotation = actionCreator<{ name: string }>('RENDERER_UPDATE_EDITABLE_NAME_ANNOTATION');
export const showPerformanceWarning = actionCreator('RENDERER_PERFORMANCE_WARNING');
export const setSelectedWallSketchElement = actionCreator<SelectedWallSketchElement | undefined>('RENDERER_SET_SELECTED_SKELETON_ELEMENT');

export type RendererProviderAction =
  | ReturnType<typeof selectPointCloud>
  | ReturnType<typeof selectMap>
  | ReturnType<typeof unSelectAll>
  | ReturnType<typeof toggleSelectedAnnotation>
  | ReturnType<typeof setSelectedAnnotations>
  | ReturnType<typeof removeSelectedAnnotations>
  | ReturnType<typeof selectEditableNameAnnotation>
  | ReturnType<typeof updateEditableNameAnnotation>
  | ReturnType<typeof showPerformanceWarning>
  | ReturnType<typeof toggleSelectedCadItem>
  | ReturnType<typeof toggleSelectedWmsItem>
  | ReturnType<typeof toggleSelectedOrthophotoItem>
  | ReturnType<typeof setSelectedWallSketchElement>;

/** If a group is selected, remove its children from the selection */
const removeChildrenOfSelectedGroups = (annotations: SelectedAnnotation[]): SelectedAnnotation[] => {
  const selectedGroupIds = new Set(annotations.filter((a) => a.type === 'group').map((a) => a.identifier));
  if (selectedGroupIds.size === 0) return annotations;
  return annotations.filter((a) => !a.groupIdentifier || !selectedGroupIds.has(a.groupIdentifier));
};

const rendererReducer = (state: RendererProviderStore = initialRendererState, action: RendererProviderAction): RendererProviderStore => {
  if (isType(action, toggleSelectedAnnotation)) {
    const currentSelectedAnnotations = state.selectedAnnotations;
    const { clearOtherSelections, ...annotation } = action.payload;
    if (action.payload.clearOtherSelections) {
      // Empty identifier = deselect all. Skip if already deselected to avoid
      // creating a new state reference (which triggers a full re-render cascade).
      if (!annotation.identifier && (currentSelectedAnnotations.length === 0 || (currentSelectedAnnotations.length === 1 && !currentSelectedAnnotations[0].identifier))) {
        return state;
      }
      return {
        ...state,
        selectedPointCloudName: undefined,
        selectedAnnotations: [annotation],
        lastSelectedAnnotation: annotation,
        mapSelected: undefined,
        selectedCadItems: [],
        selectedWmsItem: undefined,
        selectedOrthophotoItem: undefined,
        selectedWallSketchElement: undefined,
      };
    }
    if (currentSelectedAnnotations.find((annotation) => annotation.identifier === action.payload.identifier)) {
      const selectedAnnotations = currentSelectedAnnotations.filter((annotation) => annotation.identifier !== action.payload.identifier);
      return {
        ...state,
        selectedAnnotations,
        lastSelectedAnnotation: selectedAnnotations[selectedAnnotations.length - 1],
      };
    }
    return {
      ...state,
      selectedAnnotations: removeChildrenOfSelectedGroups([...currentSelectedAnnotations, annotation]),
      lastSelectedAnnotation: annotation,
    };
  }
  if (isType(action, toggleSelectedCadItem)) {
    // Note: despite the name, this is a single-select (always replaces with one item).
    // If multi-select is added, apply a group/child conflict filter like removeChildrenOfSelectedGroups.
    const cadItem = action.payload;
    return {
      ...state,
      selectedPointCloudName: undefined,
      selectedAnnotations: [],
      mapSelected: undefined,
      selectedCadItems: [cadItem],
      selectedWmsItem: undefined,
      selectedOrthophotoItem: undefined,
    };
  }
  if (isType(action, toggleSelectedWmsItem)) {
    const wmsItem = action.payload;
    return {
      ...state,
      selectedPointCloudName: undefined,
      selectedAnnotations: [],
      mapSelected: undefined,
      selectedCadItems: [],
      selectedWmsItem: wmsItem,
      selectedOrthophotoItem: undefined,
    };
  }
  if (isType(action, toggleSelectedOrthophotoItem)) {
    const orthophotoItem = action.payload;
    return {
      ...state,
      selectedPointCloudName: undefined,
      selectedAnnotations: [],
      mapSelected: undefined,
      selectedCadItems: [],
      selectedWmsItem: undefined,
      selectedOrthophotoItem: orthophotoItem,
    };
  }
  if (isType(action, mergeSelectedAnnotations)) {
    return {
      ...state,
      selectedAnnotations: removeChildrenOfSelectedGroups(uniqBy((annotation) => annotation.identifier, [...state.selectedAnnotations, ...action.payload.annotations])),
      lastSelectedAnnotation: action.payload.annotations[action.payload.annotations.length - 1],
    };
  }
  if (isType(action, setSelectedAnnotations)) {
    const annotations = removeChildrenOfSelectedGroups(uniqBy((annotation) => annotation.identifier, action.payload.annotations));
    // Replacing an empty selection with another empty one must not create a new
    // state reference (same rationale as the empty-identifier toggle above).
    if (annotations.length === 0 && state.selectedAnnotations.length === 0) return state;
    return {
      ...state,
      selectedPointCloudName: undefined,
      selectedAnnotations: annotations,
      lastSelectedAnnotation: annotations[annotations.length - 1],
      mapSelected: undefined,
      selectedCadItems: [],
      selectedWmsItem: undefined,
      selectedOrthophotoItem: undefined,
      selectedWallSketchElement: undefined,
    };
  }
  if (isType(action, removeSelectedAnnotations)) {
    const remove = new Set(action.payload.identifiers);
    if (remove.size === 0) return state;
    const selectedAnnotations = state.selectedAnnotations.filter((annotation) => !remove.has(annotation.identifier));
    if (selectedAnnotations.length === state.selectedAnnotations.length) return state;
    return {
      ...state,
      selectedAnnotations,
      lastSelectedAnnotation: selectedAnnotations[selectedAnnotations.length - 1],
    };
  }
  if (isType(action, selectPointCloud)) {
    localStorage.setItem('lastSelectedPointCloudName', action.payload.name || '');
    return {
      ...state,
      selectedPointCloudName: action.payload.name,
      selectedAnnotations: [],
      mapSelected: undefined,
      selectedCadItems: [],
      selectedWmsItem: undefined,
      selectedOrthophotoItem: undefined,
    };
  }
  if (isType(action, selectEditableNameAnnotation)) {
    return {
      ...state,
      editableNameAnnotation: action.payload,
    };
  }
  if (isType(action, updateEditableNameAnnotation)) {
    if (!state.editableNameAnnotation) return state;
    return {
      ...state,
      editableNameAnnotation: { ...state.editableNameAnnotation, name: action.payload.name },
    };
  }
  if (isType(action, setSelectedWallSketchElement)) {
    return {
      ...state,
      selectedWallSketchElement: action.payload,
    };
  }
  if (isType(action, unSelectAll)) {
    return initialRendererState;
  }
  if (isType(action, selectMap)) {
    return {
      ...state,
      selectedPointCloudName: undefined,
      selectedAnnotations: [],
      mapSelected: true,
      selectedCadItems: [],
      selectedWmsItem: undefined,
      selectedOrthophotoItem: undefined,
    };
  }
  if (isType(action, showPerformanceWarning)) {
    return {
      ...state,
      performanceWarning: true,
    };
  }

  return state;
};

export default rendererReducer;
